import Papa from 'papaparse';
import { collection, doc } from 'firebase/firestore';

/**
 * Parse CSV file and return records
 * @param {File} file - The CSV file to parse
 * @returns {Promise<Array>} Parsed CSV records
 */
export function parseCSVFile(file) {
    return new Promise((resolve, reject) => {
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            transformHeader: (header) => header.trim(),
            transform: (value) => value.trim(),
            complete: (results) => {
                if (results.errors.length > 0) {
                    console.warn('CSV parsing warnings:', results.errors);
                }
                resolve(results.data || []);
            },
            error: (error) => {
                reject(new Error(`CSV parsing failed: ${error.message}`));
            }
        });
    });
}

/**
 * Complete CSV import workflow: parse file + process + sync
 * @param {File} file - The CSV file to import
 * @param {object} db - Firestore db instance
 * @param {string} collectionPath - Firestore collection path
 * @param {function} getUnique - Function to get unique identifier from record
 * @param {Array} existingRecords - Current records in collection
 * @param {function} [onProgress] - Optional progress callback
 * @param {function} [validateRecord] - Optional record validation
 * @param {function} [canDelete] - Optional delete permission check
 * @param {function} [transformRecord] - Optional record transformation
 * @returns {Promise<{added: number, updated: number, deleted: number, errors: string[]}>}
 */
export async function importCSVFile(file, db, collectionPath, getUnique, existingRecords, onProgress, validateRecord, canDelete, transformRecord) {
    try {
        // Parse the CSV file
        const csvRecords = await parseCSVFile(file);
        
        if (!Array.isArray(csvRecords) || csvRecords.length === 0) {
            throw new Error('No valid records found in CSV file');
        }

        // Transform records if transformer provided
        const processedRecords = transformRecord 
            ? csvRecords.map(transformRecord)
            : csvRecords;

        // Import the processed records
        return await importAndSyncCollectionFromCSV(
            processedRecords, 
            db, 
            collectionPath, 
            getUnique, 
            existingRecords, 
            onProgress, 
            validateRecord, 
            canDelete
        );
    } catch (error) {
        throw new Error(`CSV import failed: ${error.message}`);
    }
}

/**
 * Common validation utilities for CSV imports
 */
export const importValidators = {
    required: (field, fieldName) => {
        if (!field || field.toString().trim() === '') {
            return `${fieldName} is required`;
        }
        return null;
    },
    
    isValidEmail: (email) => {
        if (!email) return null; // Optional field
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            return 'Invalid email format';
        }
        return null;
    },
    
    isValidNumber: (value, fieldName, min = null, max = null) => {
        if (!value) return null; // Optional field
        const num = parseFloat(value);
        if (isNaN(num)) {
            return `${fieldName} must be a valid number`;
        }
        if (min !== null && num < min) {
            return `${fieldName} must be at least ${min}`;
        }
        if (max !== null && num > max) {
            return `${fieldName} must be at most ${max}`;
        }
        return null;
    },
    
    isValidDate: (dateStr, fieldName) => {
        if (!dateStr) return null; // Optional field
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) {
            return `${fieldName} must be a valid date`;
        }
        return null;
    },
    
    isInList: (value, validOptions, fieldName) => {
        if (!value) return null; // Optional field
        if (!validOptions.includes(value)) {
            return `${fieldName} must be one of: ${validOptions.join(', ')}`;
        }
        return null;
    }
};

/**
 * SAFE Import: Only add/update records from CSV, never delete existing data.
 * @param {Array} processedData - The processed CSV data.
 * @param {object} db - Firestore db instance.
 * @param {string} collectionPath - Firestore collection path.
 * @param {function} getUnique - Function to get a unique string from a record (e.g., Customer Name, opportunity number).
 * @param {object[]} existingRecords - Array of current records in Firestore (with id and data).
 * @param {function} [onProgress] - Optional progress callback.
 * @param {function} [validateRecord] - Optional function to validate each record before processing.
 * @param {function} [generateNumberFn] - Optional function to generate unique numbers.
 * @returns {Promise<{added: number, updated: number, errors: string[]}>}
 */
export function importCollectionFromCSV(processedData, db, collectionPath, getUnique, existingRecords, onProgress, validateRecord, generateNumberFn) {
    return processCollectionSafely(processedData, db, collectionPath, getUnique, existingRecords, onProgress, validateRecord, generateNumberFn);
}

/**
 * DESTRUCTIVE Import: Import and sync a collection from CSV (WILL DELETE records not in CSV).
 * Use with extreme caution - only for full data replacement scenarios.
 * @param {Array} processedData - The processed CSV data.
 * @param {object} db - Firestore db instance.
 * @param {string} collectionPath - Firestore collection path.
 * @param {function} getUnique - Function to get a unique string from a record (e.g., Customer Name, opportunity number).
 * @param {object[]} existingRecords - Array of current records in Firestore (with id and data).
 * @param {function} [onProgress] - Optional progress callback.
 * @param {function} [validateRecord] - Optional function to validate each record before processing.
 * @param {function} [canDelete] - Function to determine if a record can be deleted (user permissions).
 * @param {function} [generateNumberFn] - Optional function to generate unique numbers.
 * @returns {Promise<{added: number, updated: number, deleted: number, errors: string[]}>}
 */
export function importAndSyncCollectionFromCSV(processedData, db, collectionPath, getUnique, existingRecords, onProgress, validateRecord, canDelete, generateNumberFn) {
    return processAndSyncCollection(processedData, db, collectionPath, getUnique, existingRecords, onProgress, validateRecord, canDelete, generateNumberFn);
}

/**
 * Safe processing - only adds and updates, never deletes
 * OPTIMIZED: Uses Firestore batch writes for efficiency
 */
async function processCollectionSafely(csvRecords, db, collectionPath, getUnique, existingRecords, onProgress, validateRecord, generateNumberFn) {
    let added = 0, updated = 0;
    const errors = [];
    const BATCH_SIZE = 500; // Firestore batch limit
    
    // Build map for quick lookup
    const existingMap = new Map(existingRecords.map(r => [getUnique(r), r]));

    // Prepare all operations first (validate and categorize)
    const operations = [];
    for (const [i, rec] of csvRecords.entries()) {
        try {
            // Validate record if validator provided
            if (validateRecord) {
                const validationError = validateRecord(rec);
                if (validationError) {
                    errors.push(`Row ${i + 1}: ${validationError}`);
                    continue;
                }
            }

            const key = getUnique(rec);
            if (!key) {
                errors.push(`Row ${i + 1}: Missing or invalid unique identifier`);
                continue;
            }

            const match = existingMap.get(key);
            if (match) {
                // Update existing record (preserve opportunity number)
                const updateData = { ...rec };
                delete updateData.opportunityNumber; // Don't overwrite existing opportunity number
                operations.push({ type: 'update', ref: doc(db, collectionPath, match.id), data: updateData, row: i + 1 });
            } else {
                // Add new record (will generate opportunity number during batch processing)
                operations.push({ type: 'add', data: rec, row: i + 1, generateNumber: !!generateNumberFn });
            }
        } catch (error) {
            errors.push(`Row ${i + 1}: ${error.message}`);
        }
    }

    // Process operations in batches
    const { writeBatch } = await import('firebase/firestore');
    let currentBatch = writeBatch(db);
    let batchCount = 0;
    let totalProcessed = 0;

    for (const op of operations) {
        try {
            if (op.type === 'update') {
                currentBatch.update(op.ref, op.data);
                updated++;
                batchCount++;
            } else if (op.type === 'add') {
                // Generate number if needed
                if (op.generateNumber && generateNumberFn) {
                    op.data.opportunityNumber = await generateNumberFn();
                }
                const newDocRef = doc(collection(db, collectionPath));
                currentBatch.set(newDocRef, op.data);
                added++;
                batchCount++;
            }

            // Commit batch when reaching limit
            if (batchCount >= BATCH_SIZE) {
                await currentBatch.commit();
                console.log(`Committed batch of ${batchCount} operations (${totalProcessed + batchCount}/${operations.length})`);
                currentBatch = writeBatch(db);
                totalProcessed += batchCount;
                batchCount = 0;
                
                if (onProgress) onProgress(totalProcessed, csvRecords.length);
            }
        } catch (error) {
            errors.push(`Row ${op.row}: ${error.message}`);
        }
    }

    // Commit remaining operations
    if (batchCount > 0) {
        await currentBatch.commit();
        console.log(`Committed final batch of ${batchCount} operations`);
        totalProcessed += batchCount;
        if (onProgress) onProgress(totalProcessed, csvRecords.length);
    }

    return { added, updated, errors };
}

/**
 * Full sync processing - can delete records not in CSV (destructive)
 * OPTIMIZED: Uses Firestore batch writes for efficiency
 */
async function processAndSyncCollection(csvRecords, db, collectionPath, getUnique, existingRecords, onProgress, validateRecord, canDelete, generateNumberFn) {
    let added = 0, updated = 0, deleted = 0;
    const errors = [];
    const BATCH_SIZE = 500; // Firestore batch limit
    
    // Build sets/maps for quick lookup
    const csvKeys = new Set();
    const existingMap = new Map(existingRecords.map(r => [getUnique(r), r]));

    // Prepare all add/update operations
    const operations = [];
    for (const [i, rec] of csvRecords.entries()) {
        try {
            // Validate record if validator provided
            if (validateRecord) {
                const validationError = validateRecord(rec);
                if (validationError) {
                    errors.push(`Row ${i + 1}: ${validationError}`);
                    continue;
                }
            }

            const key = getUnique(rec);
            if (!key) {
                errors.push(`Row ${i + 1}: Missing or invalid unique identifier`);
                continue;
            }

            csvKeys.add(key);
            const match = existingMap.get(key);
            if (match) {
                // Update existing record (preserve opportunity number)
                const updateData = { ...rec };
                delete updateData.opportunityNumber; // Don't overwrite existing opportunity number
                operations.push({ type: 'update', ref: doc(db, collectionPath, match.id), data: updateData, row: i + 1 });
            } else {
                // Add new record
                operations.push({ type: 'add', data: rec, row: i + 1, generateNumber: !!generateNumberFn });
            }
        } catch (error) {
            errors.push(`Row ${i + 1}: ${error.message}`);
        }
    }

    // Add delete operations for records not in CSV
    if (canDelete) {
        for (const r of existingRecords) {
            const key = getUnique(r);
            if (key && !csvKeys.has(key) && canDelete(r)) {
                operations.push({ type: 'delete', ref: doc(db, collectionPath, r.id), key });
            }
        }
    }

    // Process all operations in batches
    const { writeBatch } = await import('firebase/firestore');
    let currentBatch = writeBatch(db);
    let batchCount = 0;
    let totalProcessed = 0;

    console.log(`Processing ${operations.length} total operations in batches of ${BATCH_SIZE}`);

    for (const op of operations) {
        try {
            if (op.type === 'update') {
                currentBatch.update(op.ref, op.data);
                updated++;
                batchCount++;
            } else if (op.type === 'add') {
                // Generate number if needed
                if (op.generateNumber && generateNumberFn) {
                    op.data.opportunityNumber = await generateNumberFn();
                }
                const newDocRef = doc(collection(db, collectionPath));
                currentBatch.set(newDocRef, op.data);
                added++;
                batchCount++;
            } else if (op.type === 'delete') {
                currentBatch.delete(op.ref);
                deleted++;
                batchCount++;
            }

            // Commit batch when reaching limit
            if (batchCount >= BATCH_SIZE) {
                await currentBatch.commit();
                console.log(`Committed batch of ${batchCount} operations (Added: ${added}, Updated: ${updated}, Deleted: ${deleted})`);
                currentBatch = writeBatch(db);
                totalProcessed += batchCount;
                batchCount = 0;
                
                if (onProgress) onProgress(totalProcessed, operations.length);
            }
        } catch (error) {
            errors.push(`Operation failed (${op.key || op.row}): ${error.message}`);
        }
    }

    // Commit remaining operations
    if (batchCount > 0) {
        await currentBatch.commit();
        console.log(`Committed final batch of ${batchCount} operations (Added: ${added}, Updated: ${updated}, Deleted: ${deleted})`);
        totalProcessed += batchCount;
        if (onProgress) onProgress(totalProcessed, operations.length);
    }

    console.log(`Import complete: Added ${added}, Updated ${updated}, Deleted ${deleted}, Errors: ${errors.length}`);

    return { added, updated, deleted, errors };
}




